import React from 'react';
import type { MetricsData } from './types';
import './Metrics.css';

interface MetricsProps {
  data: MetricsData;
  isEnableSkeleton: boolean;
  className?: string;
}

const getEfficiencyColorClass = (efficiency: number): string =>
{
  if (efficiency >= 70) return 'efficiency-green';
  if (efficiency >= 60) return 'efficiency-yellow';
  return 'efficiency-red';
};

const Metrics: React.FC<MetricsProps> = ({ data, isEnableSkeleton, className = '' }) =>
{
  const colorRemaining = data.remaining > data.daysleft * 8 * data.numEmployees;
  const efficiencyColorClass = getEfficiencyColorClass(data.timeEfficiency);

  return (
    <div className={`meview-metrics ${className}`}>
      <div className="productivity-header">
        <h3>Sprint ends</h3>
        {isEnableSkeleton ? (
          <div className="skeleton-bar skeleton-bar-short-right" />
        ) : (
          <span className="sprint-days">
            From : {data.startDate?.replace(/\//g, '-') ?? ''} To :{' '}
            {data.dueDate?.replace(/\//g, '-') ?? ''}
          </span>
        )}
      </div>

      <div className="productivity-metrics">
        {/* Circular Progress */}
        <div className="metric-item chart-progress">
          <svg viewBox="0 0 36 36" className="circular-chart">
            <path
              className="circle-bg"
              d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
            />
            <path
              className="circle"
              strokeDasharray={`${data.progress}, 100`}
              d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
            />
          </svg>
          <div className="progress-text">
            {isEnableSkeleton ? (
              <div className="skeleton-bar skeleton-bar-large" />
            ) : (
              <div>
                <span className="progress-value">{data.progress}%</span>
                <span className="progress-label">Progress</span>
              </div>
            )}
          </div>
        </div>

        <div className="metric-grid">
          {/* Story Points */}
          {data.committedSPs > 0 && (
            <div className="story-points-summary">
              <div className="sp-header">
                <span className="sp-title">Story Points</span>
                {isEnableSkeleton ? (
                  <div className="skeleton-bar skeleton-bar-short" />
                ) : (
                  <span className="sp-count">
                    {data.completedSPs}/{data.committedSPs}
                  </span>
                )}
              </div>
              <div className="progress-bar-container">
                <div
                  className="progress-bar-fill"
                  style={{ width: `${(data.completedSPs / data.committedSPs) * 100}%` }}
                />
              </div>
            </div>
          )}

          {/* Completed Tasks */}
          <div className="metric-card">
            <div className="metric-header">
              <svg
                className="metric-icon check-icon"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
              >
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                <polyline points="22 4 12 14.01 9 11.01" />
              </svg>
              <span className="metric-label">Completed Tasks</span>
            </div>
            {isEnableSkeleton ? (
              <div className="skeleton-bar skeleton-bar-medium" />
            ) : (
              <p className="metric-value">
                {data.completedAssignedTasks}/{data.assignedTasks}
              </p>
            )}
          </div>

          {/* Rework */}
          <div className="metric-card">
            <div className="metric-header">
              <svg
                className="metric-icon alert-icon"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              <span className="metric-label">Rework</span>
            </div>
            {isEnableSkeleton ? (
              <div className="skeleton-bar skeleton-bar-medium" />
            ) : (
              <p className="metric-value rework-value">{data.reworkbytask}%</p>
            )}
          </div>

          {/* Time section */}
          <div className="metric-long-card">
            <div className="metric-header">
              <svg
                className="metric-icon clock-icon"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
              >
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
              <span className="metric-label">Time</span>
            </div>

            {isEnableSkeleton ? (
              <div className="skeleton-bar skeleton-large" />
            ) : (
              <div className="metric-row">
                <span className="metric-row-label">Actual Capacity</span>
                <span className="metric-row-value">{data.actualCapacity} h</span>
              </div>
            )}

            {isEnableSkeleton ? (
              <div className="skeleton-bar skeleton-large" />
            ) : (
              <div className="metric-row">
                <span className="metric-row-label">Total Sprint Capacity</span>
                <span className="metric-row-value">{data.totalSprintCapacity} h</span>
              </div>
            )}

            {isEnableSkeleton ? (
              <div className="skeleton-bar skeleton-large" />
            ) : (
              <div className="metric-row">
                <span className="metric-row-label">Logged Hours</span>
                <span className="metric-row-value">{data.loggedHours} h</span>
              </div>
            )}

            {isEnableSkeleton ? (
              <div className="skeleton-bar skeleton-large" />
            ) : (
              <div className="metric-row">
                <span className="metric-row-label">Estimated</span>
                <span className="metric-row-value">{data.estimated} h</span>
              </div>
            )}

            {isEnableSkeleton ? (
              <div className="skeleton-bar skeleton-large" />
            ) : (
              <div className="metric-row">
                <span className="metric-row-label">Remaining</span>
                <span className={`metric-row-value${colorRemaining ? ' color-remaining' : ''}`}>
                  {data.remaining} h
                </span>
              </div>
            )}

            {isEnableSkeleton ? (
              <div className="skeleton-bar skeleton-large" />
            ) : (
              <div className="metric-row">
                <span
                  className="metric-row-label"
                  title={
                    data.committedSPs > 0 ? 'Capacity / CompleteSP' : 'Capacity / CompleteTasks'
                  }
                >
                  {data.committedSPs > 0 ? 'Time Per SP' : 'Time Per Task'}
                </span>
                <span className="metric-row-value">
                  {data.committedSPs > 0
                    ? data.timePerSP === 'N/A'
                      ? 'N/A'
                      : `${data.timePerSP} h/sp`
                    : data.timePerTask === 'N/A'
                      ? 'N/A'
                      : `${data.timePerTask} h/task`}
                </span>
              </div>
            )}

            {isEnableSkeleton ? (
              <div className="skeleton-bar skeleton-large" />
            ) : (
              <div className="metric-row">
                <span
                  className="metric-row-label"
                  title="(Estimated-Remaining) / Logged Hours * 100"
                >
                  Efficiency
                </span>
                <span className={`metric-row-value ${efficiencyColorClass}`}>
                  {data.timeEfficiency} %
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Metrics;
